import type { GridAction } from '../../contracts/grid-action.interface';
import type { GridRow } from '../../contracts/grid-row.type';
import type {
  GridRowKeyFn,
  GridRowSelectionMode,
  GridSelectionDescriptor,
  GridSelectionScope,
  GridSelectionType
} from '../../contracts/grid-row-selection.interface';

/** Width of the leading selection checkbox column in pixels. */
export const DATA_GRID_SELECTION_COLUMN_WIDTH: number = 40;

/** Maximum number of action buttons rendered inline before the overflow menu. */
export const DATA_GRID_INLINE_ACTION_LIMIT: number = 3;

/**
 * Keyboard modifier subset needed by multi-selection.
 * Keeps the utility testable without constructing full DOM events.
 */
export interface GridSelectionModifiers {
  /** Whether the Ctrl key (Windows/Linux) was held. */
  readonly ctrlKey: boolean;
  /** Whether the Meta/Cmd key (macOS) was held. */
  readonly metaKey: boolean;
  /** Whether the Shift key was held. */
  readonly shiftKey: boolean;
}

/** Result of a multi-selection computation. */
export interface GridMultiSelectResult<T> {
  /** The updated selection after applying the modifier logic. */
  readonly newSelection: Array<T>;
  /** The index of the clicked item, stored as the Shift-range anchor. */
  readonly newLastIndex: number;
}

/**
 * Stringifies a row field into a selection key.
 *
 * @param value - Raw field value.
 * @returns A non-empty string key, or null when the value cannot identify a row.
 */
export function stringifyRowKey(value: unknown): string | null {
  if (typeof value === 'string' && value.length > 0) {
    return value;
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }
  return null;
}

/**
 * Resolves a stable identity for a row.
 *
 * Order: `rowKeyFn` → `rowKeyField` → `id` → `key`.
 *
 * @param row - Data row.
 * @param rowKeyFn - Optional consumer-provided key function.
 * @param rowKeyField - Optional field name to read.
 * @returns A key string, or null when nothing resolves.
 */
export function resolveRowKey(
  row: GridRow,
  rowKeyFn: GridRowKeyFn | null,
  rowKeyField: string
): string | null {
  if (rowKeyFn !== null) {
    const custom: string = rowKeyFn(row);
    return custom.length > 0 ? custom : null;
  }
  if (rowKeyField.length > 0) {
    return stringifyRowKey(row[rowKeyField]);
  }
  const fromId: string | null = stringifyRowKey(row['id']);
  if (fromId !== null) {
    return fromId;
  }
  return stringifyRowKey(row['key']);
}

/**
 * Whether the first row in a set can resolve a selection key.
 *
 * @param rows - Currently loaded rows.
 * @param rowKeyFn - Optional consumer-provided key function.
 * @param rowKeyField - Optional field name to read.
 * @returns True when there are no rows, or the first row resolves.
 */
export function canResolveRowKey(
  rows: ReadonlyArray<GridRow>,
  rowKeyFn: GridRowKeyFn | null,
  rowKeyField: string
): boolean {
  if (rows.length === 0) {
    return true;
  }
  return resolveRowKey(rows[0], rowKeyFn, rowKeyField) !== null;
}

/**
 * Restricts a selection to the active mode.
 *
 * @param selection - Candidate selection.
 * @param mode - Active selection mode.
 * @returns Empty when mode is `none`; last item only when `single`; otherwise a copy.
 */
export function applySelectionMode<T>(selection: ReadonlyArray<T>, mode: GridRowSelectionMode): Array<T> {
  if (mode === 'none') {
    return new Array<T>();
  }
  if (mode === 'single') {
    if (selection.length === 0) {
      return new Array<T>();
    }
    return [selection[selection.length - 1]];
  }
  return [...selection];
}

/**
 * Computes the new selection after a click, honoring Drive/OneDrive modifiers.
 *
 * | Modifier        | Behavior                                                    |
 * |-----------------|-------------------------------------------------------------|
 * | None            | Select only the clicked item (deselect everything else).    |
 * | Ctrl/Cmd        | Toggle the clicked item without affecting other selections. |
 * | Shift           | Replace selection with the contiguous range from the anchor.|
 * | Shift+Ctrl/Cmd  | Add the contiguous range to the existing selection (union). |
 *
 * @param items - The ordered list of items currently displayed.
 * @param clickedIndex - The index of the item that was clicked.
 * @param modifiers - Keyboard modifier state at the time of the click.
 * @param currentSelection - The items that are currently selected.
 * @param lastClickedIndex - The anchor index from the previous click.
 * @param getKey - Unique key for an item.
 * @returns The new selection array and the new anchor index.
 */
export function computeGridMultiSelection<T>(
  items: ReadonlyArray<T>,
  clickedIndex: number,
  modifiers: GridSelectionModifiers,
  currentSelection: ReadonlyArray<T>,
  lastClickedIndex: number | null,
  getKey: (item: T) => string
): GridMultiSelectResult<T> {
  if (items.length === 0 || clickedIndex < 0 || clickedIndex >= items.length) {
    return { newSelection: new Array<T>(), newLastIndex: -1 };
  }

  const clickedItem: T = items[clickedIndex];
  const isCtrlOrCmd: boolean = modifiers.ctrlKey || modifiers.metaKey;

  if (modifiers.shiftKey) {
    const anchor: number = lastClickedIndex !== null && lastClickedIndex >= 0 ? lastClickedIndex : 0;
    const rangeStart: number = Math.min(anchor, clickedIndex);
    const rangeEnd: number = Math.max(anchor, clickedIndex);
    const rangeItems: Array<T> = [...items.slice(rangeStart, rangeEnd + 1)];

    if (isCtrlOrCmd) {
      const existingKeys: Set<string> = new Set<string>(
        currentSelection.map((item: T): string => getKey(item))
      );
      const merged: Array<T> = [...currentSelection];
      for (const rangeItem of rangeItems) {
        if (!existingKeys.has(getKey(rangeItem))) {
          merged.push(rangeItem);
        }
      }
      return { newSelection: merged, newLastIndex: anchor };
    }

    return { newSelection: rangeItems, newLastIndex: anchor };
  }

  if (isCtrlOrCmd) {
    const clickedKey: string = getKey(clickedItem);
    const isAlreadySelected: boolean = currentSelection.some(
      (item: T): boolean => getKey(item) === clickedKey
    );

    if (isAlreadySelected) {
      return {
        newSelection: currentSelection.filter((item: T): boolean => getKey(item) !== clickedKey),
        newLastIndex: clickedIndex
      };
    }

    return {
      newSelection: [...currentSelection, clickedItem],
      newLastIndex: clickedIndex
    };
  }

  return { newSelection: [clickedItem], newLastIndex: clickedIndex };
}

/**
 * Whether a grid action should render for the current selection.
 *
 * @param action - Action definition.
 * @param descriptor - Live selection descriptor.
 * @returns True when the action is visible.
 */
export function isGridActionVisible(action: GridAction, descriptor: GridSelectionDescriptor): boolean {
  if (action.visible === undefined) {
    return true;
  }
  return action.visible(descriptor);
}

/**
 * Whether a grid action is enabled for the current selection type.
 * Mirrors `d-grid.checkSelectionTypeShouldEnableButton`.
 *
 * @param action - Action definition.
 * @param selectionType - Current cardinality.
 * @param descriptor - Live selection descriptor.
 * @returns True when the button should be enabled.
 */
export function isGridActionEnabled(
  action: GridAction,
  selectionType: GridSelectionType,
  descriptor: GridSelectionDescriptor
): boolean {
  const types: ReadonlyArray<GridSelectionType> = action.enabledForSelectionTypes;
  const typeOk: boolean = types.length === 0 || types.includes(selectionType);
  if (!typeOk) {
    return false;
  }
  if (action.disabled !== undefined && action.disabled(descriptor)) {
    return false;
  }
  return true;
}

/**
 * Splits visible actions into inline buttons and overflow-menu items.
 *
 * @param actions - Consumer-defined actions.
 * @param descriptor - Live selection descriptor.
 * @returns Inline actions (up to {@link DATA_GRID_INLINE_ACTION_LIMIT}) and overflow actions.
 */
export function partitionGridActions(
  actions: ReadonlyArray<GridAction>,
  descriptor: GridSelectionDescriptor
): { inline: Array<GridAction>; overflow: Array<GridAction> } {
  const visible: Array<GridAction> = actions.filter((action: GridAction): boolean =>
    isGridActionVisible(action, descriptor)
  );
  const forcedOverflow: Array<GridAction> = visible.filter(
    (action: GridAction): boolean => action.overflow === true
  );
  const eligibleInline: Array<GridAction> = visible.filter(
    (action: GridAction): boolean => action.overflow !== true
  );
  const inline: Array<GridAction> = eligibleInline.slice(0, DATA_GRID_INLINE_ACTION_LIMIT);
  const overflow: Array<GridAction> = [
    ...eligibleInline.slice(DATA_GRID_INLINE_ACTION_LIMIT),
    ...forcedOverflow
  ];
  return { inline, overflow };
}

/**
 * Whether `path` is covered by a selected group scope (exact match or descendant).
 *
 * @param path - Leaf or group path.
 * @param scopePath - Selected scope path.
 * @returns True when path is the scope or nested under it.
 */
export function isPathCoveredByScope(path: string, scopePath: string): boolean {
  if (path === scopePath) {
    return true;
  }
  return path.startsWith(`${scopePath}///`);
}

/**
 * Whether two selection snapshots describe the same selection for consumers.
 * Ignores materialised `rows` object identity so query-context ticks do not
 * look like a user change.
 *
 * @param left - First descriptor.
 * @param right - Second descriptor.
 * @returns True when mode, keys, scopes, query identity, and count match.
 */
export function areSelectionDescriptorsEqual(
  left: GridSelectionDescriptor,
  right: GridSelectionDescriptor
): boolean {
  return (
    left.mode === right.mode &&
    left.estimatedCount === right.estimatedCount &&
    areStringListsEqualAsSets(left.includedKeys, right.includedKeys) &&
    areStringListsEqualAsSets(left.excludedKeys, right.excludedKeys) &&
    areScopeListsEqual(left.scopes, right.scopes) &&
    areScopeListsEqual(left.excludedScopes, right.excludedScopes) &&
    areStringListsEqual(left.groupByFields, right.groupByFields) &&
    JSON.stringify(left.columnFilters) === JSON.stringify(right.columnFilters)
  );
}

/**
 * Whether two string lists contain the same values, ignoring order.
 *
 * @param left - First list.
 * @param right - Second list.
 * @returns True when both lists are set-equal.
 */
function areStringListsEqualAsSets(left: ReadonlyArray<string>, right: ReadonlyArray<string>): boolean {
  if (left.length !== right.length) {
    return false;
  }
  const leftSet: Set<string> = new Set<string>(left);
  const rightSet: Set<string> = new Set<string>(right);
  if (leftSet.size !== rightSet.size) {
    return false;
  }
  return left.every((value: string): boolean => rightSet.has(value));
}

/**
 * Whether two string lists are equal in order and value.
 *
 * @param left - First list.
 * @param right - Second list.
 * @returns True when both lists match.
 */
function areStringListsEqual(left: ReadonlyArray<string>, right: ReadonlyArray<string>): boolean {
  if (left.length !== right.length) {
    return false;
  }
  return left.every((value: string, index: number): boolean => value === right[index]);
}

/**
 * Whether two scope lists match by path, field, and estimated count.
 *
 * @param left - First list.
 * @param right - Second list.
 * @returns True when both lists match in order.
 */
function areScopeListsEqual(
  left: ReadonlyArray<GridSelectionScope>,
  right: ReadonlyArray<GridSelectionScope>
): boolean {
  if (left.length !== right.length) {
    return false;
  }
  return left.every((scope: GridSelectionScope, index: number): boolean => {
    const other: GridSelectionScope | undefined = right[index];
    return (
      other !== undefined &&
      scope.path === other.path &&
      scope.field === other.field &&
      scope.estimatedCount === other.estimatedCount
    );
  });
}
